import { useSnackbar } from "notistack";
import { useEffect } from "react";

import { useVideoEditorForm } from "./VideoEditor";
import { VideoEditorDialog } from "./VideoEditorDialog";

import { updateVideo } from "@/services/video";
import { useMutation, useQueryClient } from "react-query";

import { configs } from "@/configs";
import { Video } from "@/models";
import { AxiosErrorWithData } from "@/client/api";

export type EditVideoDialogProps = {
  open: boolean;
  data?: Video;
  onClose: () => void;
  id: string;
  fetchKey: string;
};

type VideoInput = Omit<Video, "id">;

export function EditVideoDialog({
  open: isOpen,
  onClose,
  id,
  data,
  fetchKey,
}: EditVideoDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { control, handleSubmit, reset } = useVideoEditorForm();

  const { mutate: update, isLoading } = useMutation(updateVideo, {
    onSuccess: async () => {
      enqueueSnackbar("บันทึก สำเร็จ", {
        variant: "success",
      });
      await queryClient.refetchQueries(fetchKey);
      close();
    },
    onError: (error: AxiosErrorWithData) => {
      console.error(error);
      enqueueSnackbar(
        error.response?.data.message ?? configs.unknownErrorMessage,
        {
          variant: "error",
        }
      );
    },
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isOpen && data) {
      reset({
        name: data.name,
        description: data.description ?? "",
        youtubeUrl: data.youtubeUrl,
        images: data.thumbnailUrl ? [{ id: "1", url: data.thumbnailUrl }] : [],
      });
      return;
    }
  }, [isOpen, data, reset]);

  function close() {
    reset();
    onClose();
  }

  const onSubmit = handleSubmit((form) => {
    const data: VideoInput = {
      name: form.name,
      description: form.description ?? "",
      youtubeUrl: form.youtubeUrl ?? "",
      thumbnailUrl: form.images.length > 0 ? form.images[0].url : "",
    };

    update({ id, data });
  });

  const title = "แก้ไขวิดีโอ";

  return (
    <VideoEditorDialog
      open={isOpen}
      title={title}
      onClose={close}
      onSubmit={onSubmit}
      loading={isLoading}
      control={control}
    />
  );
}
